import type { Checkpoint } from './checkpoints';

/** Start's one-sentence purpose, which seeds the Gate 1 Outcome (journey spec §3.1). */
export const JOURNEY_PURPOSE_MAX_LENGTH = 200;

export const JOURNEY_STATES = [
  'gate1',
  'requirements',
  'agent',
  'setup',
  'apply',
  'ready',
] as const;
export type JourneyState = (typeof JOURNEY_STATES)[number];

export const JOURNEY_STEPS: ReadonlyArray<{
  id: 'start' | JourneyState;
  title: string;
  gate?: string;
}> = [
  { id: 'start', title: 'Start' },
  { id: 'gate1', title: 'Classify and plan', gate: 'Gate 1' },
  { id: 'requirements', title: 'Requirements', gate: 'Gate 2' },
  { id: 'setup', title: 'Setup review', gate: 'Gates 2 + 3' },
  { id: 'apply', title: 'Apply setup', gate: 'Gate 4' },
  { id: 'ready', title: 'Build' },
];

export type CheckpointPull = {
  number: number;
  checkpoint: Checkpoint;
  state: 'open' | 'closed';
  merged: boolean;
  /** Any current-head review is "changes requested". */
  changesRequested?: boolean;
  html_url?: string;
  created_at?: string;
};

/** A step is a task, a pending decision, or waiting on an agent or workflow. */
export type StepKind = 'task' | 'decision' | 'waiting' | 'done';

export type JourneyStatus = {
  state: JourneyState;
  kind: StepKind;
  /** The open PR for the current step, if any. */
  pull?: CheckpointPull;
  changesRequested: boolean;
};

const latest = (pulls: readonly CheckpointPull[], checkpoint: Checkpoint) =>
  pulls
    .filter(p => p.checkpoint === checkpoint)
    .sort((a, b) => b.number - a.number)[0];

const merged = (pulls: readonly CheckpointPull[], checkpoint: Checkpoint) =>
  pulls.some(p => p.checkpoint === checkpoint && p.merged);

const openOf = (pulls: readonly CheckpointPull[], checkpoint: Checkpoint) => {
  const pull = latest(pulls, checkpoint);
  return pull?.state === 'open' ? pull : undefined;
};

function decisionOrTask(
  state: JourneyState,
  pull: CheckpointPull | undefined,
): JourneyStatus {
  if (!pull) return { state, kind: 'task', changesRequested: false };
  const changesRequested = Boolean(pull.changesRequested);
  return {
    state,
    kind: changesRequested ? 'task' : 'decision',
    pull,
    changesRequested,
  };
}

/**
 * The journey state, derived from checkpoint PRs only (journey spec §2).
 * Nothing stores it separately.
 */
export function deriveJourneyState(
  pulls: readonly CheckpointPull[],
): JourneyStatus {
  if (!merged(pulls, 'gate1')) {
    return decisionOrTask('gate1', openOf(pulls, 'gate1'));
  }
  if (!merged(pulls, 'requirements')) {
    return decisionOrTask('requirements', openOf(pulls, 'requirements'));
  }
  if (!merged(pulls, 'setup-output')) {
    const setup = openOf(pulls, 'setup-output');
    return setup
      ? decisionOrTask('setup', setup)
      : { state: 'agent', kind: 'waiting', changesRequested: false };
  }
  if (!merged(pulls, 'apply-config')) {
    const apply = openOf(pulls, 'apply-config');
    return apply
      ? decisionOrTask('apply', apply)
      : { state: 'apply', kind: 'waiting', changesRequested: false };
  }
  return { state: 'ready', kind: 'done', changesRequested: false };
}

export const journeyStepIndex = (state: JourneyState) =>
  JOURNEY_STEPS.findIndex(step => step.id === state);
